import { useEffect, useState } from 'react'
import { STATUS_OPTIONS } from '../lib/constants'
import { formatLongDate } from '../lib/date'
import { CheckIcon, TrashIcon } from './Icons'
import Modal from './Modal'

export default function EntryModal({ resource, day, entry, saving, onClose, onSave }) {
  const [status, setStatus] = useState(entry?.status || 'worked')
  const [hours, setHours] = useState(entry?.status === 'worked' ? String(entry.hours ?? 8) : '8')
  const [note, setNote] = useState(entry?.note || '')
  const [error, setError] = useState('')

  useEffect(() => {
    setStatus(entry?.status || 'worked')
    setHours(entry?.status === 'worked' ? String(entry.hours ?? 8) : '8')
    setNote(entry?.note || '')
    setError('')
  }, [entry, resource.id, day.key])

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')

    if (status === 'worked') {
      const parsedHours = Number(hours)
      if (!Number.isFinite(parsedHours) || parsedHours <= 0 || parsedHours > 24) {
        setError('Enter worked hours greater than 0 and no more than 24.')
        return
      }
    }

    try {
      await onSave({
        resource_id: resource.id,
        work_date: day.key,
        status,
        hours: status === 'worked' ? Number(hours) : null,
        note,
      })
    } catch (saveError) {
      setError(saveError.message || 'The day could not be saved.')
    }
  }

  async function handleClear() {
    setError('')
    try {
      await onSave({
        resource_id: resource.id,
        work_date: day.key,
        status: null,
        hours: null,
        note: '',
      })
    } catch (saveError) {
      setError(saveError.message || 'The day could not be cleared.')
    }
  }

  return (
    <Modal
      className="entry-modal"
      onClose={onClose}
      subtitle={`${resource.name} · ${formatLongDate(day.date)}`}
      title="Update day"
    >
      <form onSubmit={handleSubmit}>
        <div className="modal-body">
          <fieldset className="status-fieldset">
            <legend>Day type</legend>
            <div className="status-choice-grid">
              {STATUS_OPTIONS.map((option) => (
                <button
                  aria-pressed={status === option.value}
                  className={`status-choice status-choice--${option.value} ${
                    status === option.value ? 'is-selected' : ''
                  }`}
                  key={option.value}
                  onClick={() => setStatus(option.value)}
                  type="button"
                >
                  <span className="status-choice-marker">
                    {option.value === 'worked' ? <CheckIcon size={17} /> : option.marker}
                  </span>
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.description}</small>
                  </span>
                </button>
              ))}
            </div>
          </fieldset>

          {status === 'worked' ? (
            <label className="field-label" htmlFor="worked-hours">
              Hours worked
              <div className="number-input-wrap">
                <input
                  autoFocus
                  id="worked-hours"
                  inputMode="decimal"
                  max="24"
                  min="0.25"
                  onChange={(event) => setHours(event.target.value)}
                  required
                  step="0.25"
                  type="number"
                  value={hours}
                />
                <span>hours</span>
              </div>
            </label>
          ) : null}

          <label className="field-label" htmlFor="entry-note">
            Note <span className="field-optional">optional</span>
            <textarea
              id="entry-note"
              maxLength="500"
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add a short note for this day"
              rows="3"
              value={note}
            />
          </label>

          {error ? <p className="form-error">{error}</p> : null}
        </div>

        <footer className="modal-footer modal-footer--split">
          <div>
            {entry ? (
              <button
                className="button button--danger-ghost"
                disabled={saving}
                onClick={handleClear}
                type="button"
              >
                <TrashIcon size={16} />
                Clear day
              </button>
            ) : null}
          </div>
          <div className="modal-footer-actions">
            <button className="button button--secondary" disabled={saving} onClick={onClose} type="button">
              Cancel
            </button>
            <button className="button button--primary" disabled={saving} type="submit">
              {saving ? 'Saving…' : 'Save day'}
            </button>
          </div>
        </footer>
      </form>
    </Modal>
  )
}
